import { Injectable } from '@angular/core';
import * as Survey from 'survey-core';
import {
  ApplicationsApiService,
  SnackbarService,
  ProductTypeCatalogApiService,
  PeopleApiService,
  PeopleLookupApiService,
  OfficesApiService,
} from '@tangilla/tg-ng-core';
import { catchError, map, of, switchMap, throwError } from 'rxjs';
import { SurveyUtilsService } from './survey.utils.service';
import { SurveyService } from 'src/app/core/services/survey/survey.service';
import { RequestParameters } from '@tangilla/tg-ng-models';
import { ErrorsService } from 'src/app/core/services/utils/errors.service';
import {
  DetailsListWidgetService
} from 'src/app/portal/form-processor/widgets/details-list/services/details-list.widget.service';
import {
  ApplicationChargesUtilsService
} from '../../../../../core/services/utils/charges/application-charges.utils.service';

@Injectable({
  providedIn: 'root'
})
export class ApplicationForm {
  formatter: Intl.NumberFormat | any;
  dr_workflow: boolean = false;
  applicationMemberType: string = 'affiliate';

  constructor(
    protected applicationsService: ApplicationsApiService,
    protected productTypeCatalogService: ProductTypeCatalogApiService,
    protected snackbarService: SnackbarService,
    protected chargesUtils: SurveyUtilsService,
    protected surveyService: SurveyService,
    protected peopleApiService: PeopleApiService,
    protected peopleLookupService: PeopleLookupApiService,
    protected errorsService: ErrorsService,
    protected officesApiService: OfficesApiService,
    protected detailsListWidgetService: DetailsListWidgetService,
    protected applicationChargesUtilsService: ApplicationChargesUtilsService,
  ) {
  }

  initializeForm(): any {
    this.formatter = new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    });

    this.setFormOnComplete();

    // The affiliate and staff form are NOT using the new available membership types so it needs to be done the old way
    this.surveyService.surveyModel.onValueChanged.add((sender, options) => {
      if (options.name === 'CorpLicName') {
        // If creating new office and is with a corporate license, set business_name q
        if (options.value && sender.getValue('Office-Search-Type') === 'NewOffice' && sender.getValue('isCorp') === 'Yes') {
          sender.setValue('business_name', options.value);
          sender.setVariable('office_name', options.value);
          const businessNameQuestion = sender.getQuestionByName('business_name');
          if (businessNameQuestion) {
            businessNameQuestion.readOnly = true;
          }
        }
      }
      if (options.name === 'LicState' && options.value && sender.getValue('LicNumber')) {
        const requestParameters = new RequestParameters(null);
        requestParameters.additionalFields = {
          include_relationships: true,
          license_number: sender.getValue('LicNumber'),
          license_state: options.value,
          exclusive: true,
        };

        this.surveyService.isLoading = true;
        this.peopleLookupService.get(requestParameters).subscribe({
          next: (response: any) => {
            if (response.length > 0) {
              const person = response[0];
              this.setPersonId(person.person_id);
            } else {
              this.setPersonId(null);
            }
            this.surveyService.isLoading = false;
          },
          error: (error: any) => {
            this.surveyService.isLoading = false;
            this.errorsService.processError(error);
          }
        });
      } else if ((options.name === 'person_id' && sender.getValue('office')) || (options.name === 'office' && sender.getValue('person_id'))) {
        if (options.name === 'office') {
          const officeIdSelected = sender.getValue('office');
          if (officeIdSelected) {
            this.surveyService.isLoading = true;
            this.officesApiService.getOffice(officeIdSelected).subscribe({
              next: (office: any) => {
                sender.setVariable('selected_office', office);
                sender.setVariable('office_name', office.business_name);
                sender.setVariable('dr_name', office.field_description.designated_realtor);
                this.surveyService.isLoading = false;
              },
              error: (error) => {
                this.surveyService.isLoading = false;
                this.errorsService.processError(error);
              }
            });
          }
        }

        this.peopleApiService.getPerson(sender.getValue('person_id'))
          .subscribe({
            next: (person: any) => {
              this.peopleApiService.getPersonRelationships(new RequestParameters(null), true, person.person_id)
                .subscribe({
                  next: (relationships) => {
                    this.surveyService.personRelationships = relationships.items;
                    if (this.surveyService.personRelationships) {
                      const relationship = this.surveyService.personRelationships.find(r => r.office_nrds_id === sender.getValue('office'));
                      if (
                        relationship &&
                        relationship.status === 'active' &&
                        (
                          relationship.role_code === sender.getValue('role') ||
                          (
                            sender.getValue('MembershipType') === 'MLSOnly' &&
                            ['R', 'MLO', 'N'].includes((relationship as any).role_type) &&
                            relationship.role_code !== 'NUA' &&
                            relationship.role_code !== 'LMX'
                          )
                        )
                      ) {

                        const that = this;

                        function stopFormPersonAlreadyApplied() {
                          const fname = sender.getValue('FirstName');
                          const title = `Hi ${fname}!`;
                          const error_message_membership = `You are already active with ${sender.getVariable('office_name')} (#${sender.getValue('office')}) for the type of membership you selected. <br /><br />` +
                            `If this is the office you want to apply for, choose a different type of membership or contact your sponsor.<br />`;
                          that.errorsService.exitForm(title, error_message_membership);
                        }

                        if (!sender.getVariable('office_name')) {
                          this.officesApiService.getOffice(sender.getValue('office')).subscribe({
                            next: (office: any) => {
                              sender.setVariable('selected_office', office);
                              sender.setVariable('office_name', office.business_name);
                              sender.setVariable('dr_name', office.field_description.designated_realtor);
                              this.surveyService.isLoading = false;
                              stopFormPersonAlreadyApplied();
                            },
                            error: (error) => {
                              this.surveyService.isLoading = false;
                              this.errorsService.processError(error);
                            }
                          });
                        } else {
                          this.surveyService.isLoading = false;
                          stopFormPersonAlreadyApplied();
                        }

                      }
                    }
                  },
                  error: (error) => {
                    this.surveyService.isLoading = false;
                    this.errorsService.processError(error);
                  }
                });
            },
            error: (error: any) => {
              this.errorsService.processError(error);
            }
          });
      }
    });

    // Here goes the navigation code for the membership application form
    this.initializeOnAfterRenderPage();
    this.initializeOnCurrentPageChanging();
    this.initializeOnValueChanged();

  }

  setFormOnComplete() {
    const that = this;

    this.surveyService.surveyModel.onComplete.add(function (result) {
      if (!that.surveyService.isPreview) {
        that.surveyService.surveyModel.setValue('Office-Search', '');
        that.surveyService.surveyModel.setValue('NRDSSearch', '');
        that.surveyService.surveyModel.setValue('AutoAddress', '');
      }
    });
  }

  initializeOnAfterRenderPage() {
  }

  initializeOnCurrentPageChanging() {
  }

  initializeOnValueChanged() {
  }

  protected urlParam(name: string) {
    var results = new RegExp('[\?&]' + name + '=([^&#]*)').exec(window.location.href);
    if (results == null) {
      return null;
    }

    return decodeURI(results[1]) || 0;
  }

  protected getAvailableMembershipTypesRequestBody() {
    return {
      license_data: {
        license_number: this.surveyService.surveyModel.getValue('LicNumber'), // || '9737890',
        license_name: this.surveyService.surveyModel.getValue('LicName'),
        license_state: this.surveyService.surveyModel.getValue('LicState'), // || 'TX',
        license_type: this.surveyService.surveyModel.getValue('LicType'), // || 'Broker',
        license_status: this.surveyService.surveyModel.getValue('LicStatus'),
        license_expiry: this.surveyService.surveyModel.getValue('LicExp'),
        broker_license_number: this.surveyService.surveyModel.getValue('BrkLic'),
        broker_license_type: this.surveyService.surveyModel.getValue('BrkLicType'),
      },
      member_nrds_id: this.surveyService.surveyModel.getValue('NRDS'),
      office_nrds_id: this.surveyService.surveyModel.getValue('office'),
      person_id: this.surveyService.surveyModel.getValue('person_id'),
      becomes_dr: this.surveyService.surveyModel.getValue('DesignatedRealtor') === 'Yes' || ['Yes', 'Transfer'].includes(this.surveyService.surveyModel.getValue('CorporateAffiliate'))
    };
  }

  protected getAvailableMembershipTypes() {
    const body = this.getAvailableMembershipTypesRequestBody();
    this.surveyService.isLoading = true;
    return this.applicationsService.getAvailableMembershipTypes(body)
      .pipe(
        map((data: any) => {

          if (data.person_id && data.person_id !== this.surveyService.surveyModel.getValue('person_id')) {
            this.surveyService.personId = data.person_id;
            this.surveyService.surveyModel.setValue('person_id', data.person_id);
          }
          if (data.member_nrds_id && data.member_nrds_id !== this.surveyService.surveyModel.getValue('NRDS')) {
            this.surveyService.surveyModel.setValue('NRDS', data.member_nrds_id);
            this.surveyService.surveyModel.setVariable('member_record', data.member_record);
          }

          this.surveyService.surveyModel.setVariable('is_already_member', data.is_member);
          this.surveyService.surveyModel.setVariable('can_create_office', data.can_create_office);

          const membership_types = data.membership_types;
          const question = this.surveyService.surveyModel.getQuestionByName('MembershipType');
          question['choices'] = membership_types.map((mt: any) => new Survey.ItemValue(mt.code, mt.description));
          if (membership_types.length === 1) {
            this.surveyService.surveyModel.setValue('MembershipType', membership_types[0].code);
            this.surveyService.surveyModel.setVariable('has_to_select_membership_type', false);
          }
          this.surveyService.isLoading = false;
        }),
        catchError((error: any) => {
          this.surveyService.isLoading = false;
          this.errorsService.processError(error);
          return throwError(error);
        })
      );
  }

  protected setRoleAndFetchProducts() {
    if (this.surveyService.surveyModel.getValue('MembershipType')) {
      if (this.surveyService.surveyModel.getValue('Office-Search-Type') === 'NewOffice') {
        this.surveyService.surveyModel.setValue('DesignatedRealtor', 'Yes');
      }

      const body = {
        membership_type: this.surveyService.surveyModel.getValue('MembershipType'),
        CA: this.surveyService.surveyModel.getValue('CorporateAffiliate'),
        lic_type: this.surveyService.surveyModel.getValue('LicType'),
        occupation: this.surveyService.surveyModel.getValue('occupation'),
        reso_office_type: this.surveyService.surveyModel.getValue('reso_office_type'),
        DR: this.surveyService.surveyModel.getValue('DesignatedRealtor'),
        state_dues_status: this.surveyService.surveyModel.getValue('StateDuesStatus'),
        key_services: this.surveyService.surveyModel.getVariable('key_services'),
        apply: this.urlParam('apply'),
        NRDS: this.surveyService.surveyModel.getValue('NRDS'),
        member_record: this.surveyService.surveyModel.getVariable('member_record'),
        inspector: this.surveyService.surveyModel.getValue('LicenseHeld')
      };

      this.surveyService.isLoading = true;
      return this.applicationsService.getRole(body, this.applicationMemberType)
        .pipe(
          map((data: any) => {
            this.surveyService.surveyModel.setValue('role', data.role);
            this.surveyService.surveyModel.setValue('association_product_type', data.association_product_type);
            this.dr_workflow = data.dr_workflow;
            this.surveyService.drWorkflow = data.dr_workflow;

            if (this.surveyService.surveyModel.getValue('person_id') && this.surveyService.surveyModel.getValue('office')) {
              // await validateIfPersonAlreadyApplied()
            }

            this.surveyService.isLoading = false;
          }),
          switchMap(() => this.getProductCatalogForRole())
        );
    } else {
      return of(null);
    }

  }

  protected getCatalogQueryParams() {
    const qp: any = {
      defaults_only: true
    };

    if (
      (
        this.surveyService.surveyModel.getValue('Office-Search-Type') === 'Broker' ||
        ['Yes', 'Already'].includes(this.surveyService.surveyModel.getValue('DesignatedRealtor')) ||
        (
          this.surveyService.surveyModel.getValue('Office-Search-Type') === 'Office' &&
          this.surveyService.surveyModel.getValue('DesignatedRealtor') === 'No'
        )
      ) &&
      this.surveyService.surveyModel.getValue('office')
    ) {
      qp.office_nrds_id = this.surveyService.surveyModel.getValue('office');
    }

    if (this.surveyService.surveyModel.getValue('NRDS')) {
      qp.member_nrds_id = this.surveyService.surveyModel.getValue('NRDS');
    }

    if (this.surveyService.surveyModel.getValue('StateDuesPaid') === 'Yes') {
      qp.exclude_state_dues = true;
    }

    if (this.surveyService.surveyModel.getValue('NationalDuesPaid') === 'Yes') {
      qp.exclude_national_dues = true;
    }

    if (this.surveyService.surveyModel.getValue('NationalDuesPaidNextYear') === 'Yes') {
      qp.exclude_national_next_year = true;
    }

    if (this.surveyService.surveyModel.getValue('StateDuesPaidNextYear') === 'Yes') {
      qp.exclude_state_next_year = true;
    }

    if (this.surveyService.surveyModel.getValue('person_id')) {
      qp.person_id = this.surveyService.surveyModel.getValue('person_id');
    }

    if (typeof this.surveyService.surveyModel.getVariable('state_dues_proration') === 'boolean') {
      qp.state_dues_proration = this.surveyService.surveyModel.getVariable('state_dues_proration');
    }

    if (typeof this.surveyService.surveyModel.getVariable('national_dues_proration') === 'boolean') {
      qp.national_dues_proration = this.surveyService.surveyModel.getVariable('national_dues_proration');
    }

    // const is_creating_new_office = req.query.is_creating_new_office
    if (['NewOffice'].includes(this.surveyService.surveyModel.getValue('Office-Search-Type'))) {
      qp.is_creating_new_office = true;
    }

    if (this.surveyService.surveyModel.getValue('license_type')) qp.license_type = this.surveyService.surveyModel.getValue('license_type');
    if (this.surveyService.surveyModel.getValue('MembershipType')) qp.membership_type = this.surveyService.surveyModel.getValue('MembershipType');
    if (this.surveyService.surveyModel.getValue('DesignatedRealtor')) qp.dr_q_value = this.surveyService.surveyModel.getValue('DesignatedRealtor');


    return qp;
  }

  protected getProductCatalogForRole() {
    const qp = this.getCatalogQueryParams();

    this.surveyService.isLoading = true;
    return this.productTypeCatalogService.getProductTypeCatalogForRoleCode(this.surveyService.surveyModel.getValue('role'), qp)
      .pipe(
        map((data: any) => {
          this.surveyService.surveyModel.setVariable('product_catalog', data);
          // Loop through catalog and make the necessary checks
          for (const product_name in data.products) {
            const product_types = data.products[product_name].product_types;
            const product_id = data.products[product_name].product_id;
            this.surveyService.surveyModel.setVariable(`${product_name}ProductID`, product_id);
            if (product_name === 'MLS' && product_types.length === 0 && this.surveyService.surveyModel.getValue('MembershipType') === 'MLSOnly') {
              const title = `We are unable to retrieve ${product_name} products for your application.`;
              const message = `This can happen when the Designated Licensee's subscription in the office you selected has been suspended. Please contact your sponsor.`;
              this.surveyService.isLoading = false;
              this.errorsService.exitForm(title, message);
              this.surveyService.surveyModel.setVariable('dr_mls_status_ok', false);
            }

            const productQuestion = this.surveyService.surveyModel.getQuestionByName(product_name + 'Products');
            if (productQuestion) {
              if (productQuestion['choices'].length > 0) {
                productQuestion['choices'].splice(0, productQuestion['choices'].length); // clear existing options from the product question
              }

              for (let x = 0; x < product_types.length; x++) {

                const item = new Survey.ItemValue(product_types[x].id, product_types[x].description);
                if (!productQuestion['choices']) {
                  productQuestion['choices'] = [];
                }
                productQuestion['choices'].push(item);

                if (product_types[x].is_coop) {
                  this.surveyService.surveyModel.setValue('coop_product', product_types[x].id);
                }
                // Add required products
                if (product_types[x].required_product_ids && product_types[x].required_product_ids.length > 0) {
                  for (const required_product_id of product_types[x].required_product_ids) {
                    let required_product;
                    let required_product_key;
                    for (const key of Object.keys(data.products)) {
                      if (data.products[key].product_id === required_product_id) {
                        required_product = data.products[key];
                        required_product_key = key;
                        break;
                      }
                    }

                    // TODO: this is a workaround for frontend of choice for TMLS because the rule is that they should all get the FENS product type on application
                    // regardless of multiple selections being allowed
                    // Their product config currently has multiple products
                    if (required_product_key === 'FOC1' && data.products[required_product_key].product_types.length > 1) {
                      data.products[required_product_key].product_types = data.products[required_product_key].product_types.filter((pt: any) => pt.id === 'FENS');
                    }

                    // Get product types of required product, if it's just one, add product type to pricing table and autoselect it
                    if (required_product_key && data.products[required_product_key].product_types.length === 1) {
                      // const required_product_product_type = data.products[required_product_key].product_types[0]
                      // setProductTypePricingTable(required_product_product_type, PricingTable, qp)
                      if (this.surveyService.surveyModel.getQuestionByName(`${product_name}Requires${required_product_key}`)) {
                        this.surveyService.surveyModel.getQuestionByName(`${product_name}Requires${required_product_key}`).value = 'Yes';
                      }
                      if (this.surveyService.surveyModel.getQuestionByName(`${required_product_key}Access`)) {
                        this.surveyService.surveyModel.getQuestionByName(`${required_product_key}Access`).value = 'Yes';
                        this.surveyService.surveyModel.getQuestionByName(`${required_product_key}Products`).value = data.products[required_product_key].product_types[0].id;
                      }

                      const product_id_question = this.surveyService.surveyModel.getQuestionByName(`${required_product_key}ProductId`);

                      if (product_id_question) {
                        product_id_question.value = required_product_id;
                      }

                    } else {
                      // Multiple selection here, the user cannot choose to not select anything in the product so we have to remove the
                      // choice to select 'No'
                      if (this.surveyService.surveyModel.getQuestionByName(`${required_product_key}Access`)) {
                        this.surveyService.surveyModel.getQuestionByName(`${required_product_key}Access`)['choices'] = [
                          {
                            'value': 'Yes',
                            'text': 'Yes'
                          },
                        ];
                        this.surveyService.surveyModel.getQuestionByName(`${required_product_key}Access`).value = 'Yes';
                      }
                    }
                  }
                }
              }
              this.surveyService.surveyModel.setVariable(`CanSelect${product_name}`, true);
              this.surveyService.isLoading = false;
            }
          }
        }),
        catchError((error: any) => {
          this.surveyService.isLoading = false;
          this.errorsService.processError(error);
          return throwError(error);
        })
      );
  }

  protected setPricingTable() {
    const data = this.surveyService.surveyModel.getVariable('product_catalog');
    let row = 0;
    for (const product_name in data.products) {

      const product = data.products[product_name];

      let title = 'Available ' + product.label + ' products';

      const that = this;

      let processedData: {
        title: string;
        items: Array<{
          id: number;
          firstValue: string;
          secondValue: string;
          details: any[];
          detailsCount: number;
          showDetails: boolean;
        }>;
        showColumnsTitles: boolean;
        withExpandButton: boolean;
        footer: string;
      } = {
        title: title,
        items: [],
        showColumnsTitles: false,
        withExpandButton: true,
        footer: '^ includes tax'
      };

      let itemId = 0;
      let detailId = 0;
      for (let x = 0; x < product.product_types.length; x++) {

        let item: {
          id: number;
          firstValue: string;
          secondValue: string;
          details: any[];
          detailsCount: number;
          showDetails: boolean;
        } = {
          id: itemId++,
          firstValue: '',
          secondValue: '',
          details: [],
          detailsCount: 0,
          showDetails: false
        };

        let product_type = product.product_types[x];

        var fee = 0;
        var totalFee = 0;
        var label = '';
        var longDescription = '';
        let detailsCount = 0;
        for (let b = 0; b < product_type.bill_periods.length; b++) {
          detailsCount++;
          let detail = {
            id: detailId,
            firstValue: '',
            secondValue: ''
          };
          if (product_type.bill_periods[b].proration_applied) {
            label = 'Prorated charges for the period ' + product_type.bill_periods[b].start_date + ' to ' + product_type.bill_periods[b].end_date;
          } else {
            label = 'Charges for the billing period ' + product_type.bill_periods[b].start_date + ' to ' + product_type.bill_periods[b].end_date;
          }
          detail = {
            id: detailId++,
            firstValue: label,
            secondValue: ''
          };
          item.details.push(detail);

          if (product_type.bill_periods[b].fees.length == 0) {
            detail = {
              id: detailId++,
              firstValue: 'There are no charges for this period.',
              secondValue: ''
            };
            item.details.push(detail);
          }

          for (let y = 0; y < product_type.bill_periods[b].fees.length; y++) {
            detailsCount++;
            fee = parseFloat(product_type.bill_periods[b].fees[y].price) + parseFloat(product_type.bill_periods[b].fees[y].tax);
            if (parseFloat(product_type.bill_periods[b].fees[y].tax) != 0) {
              label = product_type.bill_periods[b].fees[y].description + ' ^ ';
            } else {
              label = product_type.bill_periods[b].fees[y].description;
            }
            detail = {
              id: detailId++,
              firstValue: label,
              secondValue: `${that.formatter.format(fee)}`
            };
            item.details.push(detail);

            totalFee += fee;
          }
        }

        if (product_type.long_description != '') { // include a product type description if one exists
          longDescription = '<br/>' + product_type.long_description;
        }

        item.firstValue = `<strong>${product_type.description}</strong> ${longDescription}`;
        item.secondValue = `<strong>${that.formatter.format(totalFee)}`;
        item.detailsCount = detailsCount;
        detailId = 0;
        processedData.items.push(item);
      }

      this.surveyService.surveyModel.setVariable(product_name + 'DetailsList', processedData);

      this.surveyService.surveyModel.setVariable('CanSelect' + product_name, true);
    }
  }

  protected buildOrderConfirmation() {
    // Call charges endpoint
    const requestBody = this.getCatalogQueryParams();
    requestBody.recn_chargeable = this.surveyService.chargeable;
    requestBody.prorrate = true;
    requestBody.role_code = this.surveyService.surveyModel.getValue('role');
    requestBody.products = [];

    // Check Association product selection
    const associationProductType = this.surveyService.surveyModel.getValue('association_product_type');
    const associationProductID = this.surveyService.surveyModel.getVariable('AssociationProductID');
    if (associationProductType) {
      requestBody.products.push({
        product_id: associationProductID,
        product_type: associationProductType
      });
    }

    // Check MLS product selection
    const mlsProductType = this.surveyService.surveyModel.getValue('MLSProducts');
    const mlsProductID = this.surveyService.surveyModel.getVariable('MLSProductID');
    if (mlsProductType) {
      requestBody.products.push({
        product_id: mlsProductID,
        product_type: mlsProductType
      });
    }

    // Check Lockbox product selection
    const lockboxProduct = this.surveyService.surveyModel.getValue('LockboxProducts');
    const lockboxProductID = this.surveyService.surveyModel.getVariable('LockboxProductID');
    if (lockboxProduct) {
      requestBody.products.push({
        product_id: lockboxProductID,
        product_type: lockboxProduct
      });
    }

    // We need to add any additional NewOffice fee and transition_election fee to the charges
    requestBody.additional_invoice_template_ids = [];
    const invoice_templates = this.surveyService.surveyModel.getVariable('invoice_templates');
    if (this.surveyService.surveyModel.getValue('Office-Search-Type') && invoice_templates[this.surveyService.surveyModel.getValue('Office-Search-Type')]) {
      requestBody.additional_invoice_template_ids.push(invoice_templates[this.surveyService.surveyModel.getValue('Office-Search-Type')]);
    }
    if (this.surveyService.surveyModel.getValue('TransitionElection') && invoice_templates['transition_election']) { // TODO: retest this!!!
      requestBody.additional_invoice_template_ids.push(invoice_templates['transition_election']);
    }

    this.surveyService.isLoading = true;
    this.applicationsService.getApplicationCharges(requestBody)
      .subscribe({
        next: (data: any) => {
          this.applicationChargesUtilsService.processApplicationCharges(data);
          this.surveyService.isLoading = false;
        },
        error: (error: any) => {
          this.errorsService.processError(error);
        }
      });
  }

  protected setPersonId(person_id: string | null) {
    this.surveyService.surveyModel.setValue('person_id', person_id);
    this.surveyService.personId = person_id;
  }

}
